import React, { useState, Suspense, useRef } from 'react';
import { useFormik } from 'formik';
import AppLoading from '../app-loading/app-loading';
import { useTranslation } from 'react-i18next';
import OrderService from '../../services/order/order.service';
import { OrderModel } from '../../models/orders/order-list-model';
import PackingNewOrderReview from './packing-order-review';
import orderServices from '../../services/orders/orderservice';
import { Toast, ToastMessage } from "primereact/toast";
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

interface OrderDropdownDTO {
    orderId: number;
    display: string;
}

interface OrderIdSelectorProps {
    formik: any;
    orders: OrderDropdownDTO[];
    packingNumber: string;
    onPackingNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onUpdatePackingNumber: () => void;
    isLoading: boolean;
    onFindOrder: () => void;
    orderIdInput: string;
    onOrderIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const OrderIdSelector: React.FC<OrderIdSelectorProps> = ({
    packingNumber,
    onPackingNumberChange,
    onUpdatePackingNumber,
    isLoading,
    onFindOrder,
    orderIdInput,
    onOrderIdChange
}) => {
    return (
        <div className="flex align-items-center gap-2 w-full">
            <label htmlFor="orderId" className="font-semibold whitespace-nowrap">
                Order ID<span className="required">*</span>
            </label>
            <InputText
                value={orderIdInput}
                onChange={onOrderIdChange}
                keyfilter="int"
                placeholder="Enter order ID"
                style={{ width: '150px' }}
            />
            <Button
                label="Find"
                onClick={onFindOrder}
                className="p-button-sm"
                style={{ width: '70px' }}
            />
            <span style={{ marginLeft: '30px' }}>Bin No.</span>
            <InputText
                value={packingNumber}
                onChange={onPackingNumberChange}
                keyfilter="int"
                placeholder="Enter Bin No"
                style={{ width: '150px' }}
            />
            <Button
                label="Save"
                onClick={onUpdatePackingNumber}
                className="p-button-sm"
                loading={isLoading}
                style={{ width: '70px' }}
            />
        </div>
    );
};

const Packingorders: React.FC = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<OrderDropdownDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [orderDetails, setOrderDetails] = useState<OrderModel | null>(null);
    const [packingNumber, setPackingNumber] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [orderIdInput, setOrderIdInput] = useState<string>("");
    const toast = useRef<Toast>(null);

    const handlePackingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
   
        if (/^\d*$/.test(value)) {   
            if (value === "0") {
                setPackingNumber("");
                showMessage('Error', 'Bin number must be greater than or equal to 1', 'error');
            } else {
                setPackingNumber(value);
            }
        }
    };

    const handleOrderIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setOrderIdInput(value);
        }
    };

    const handleFindOrder = async () => {
        if (!orderIdInput) {
            showMessage('Error', 'Please enter an order ID', 'error');
            setOrderDetails(null);
            return;
        }

        const orderId = Number(orderIdInput);
        if (isNaN(orderId)) {
            showMessage('Error', 'Please enter a valid order ID', 'error');
            setOrderDetails(null);
            return;
        }

        try {
            setOrderDetails(null);
            const fetchedOrders = await fetchOrders(orderId);
            if (fetchedOrders.length > 0) {
                formik.setFieldValue("orderId", fetchedOrders[0].orderId);
                await handleOrderSelect(fetchedOrders[0].orderId);
            } else {
                showMessage('Warning', 'No orders found with this ID', 'warn');
                setOrderDetails(null);
            }
        } catch (error) {
            console.error('Error finding order:', error);
            showMessage('Error', 'Failed to find order', 'error');
            setOrderDetails(null);
        }
    };

    const fetchOrders = async (orderId: number): Promise<OrderDropdownDTO[]> => {
        setLoading(true);
        try {
            const response = await OrderService.searchOrdersById(orderId);
            const mappedOrders = Array.isArray(response)
                ? response.map((order: any) => ({
                    orderId: order.orderID,
                    display: `Order #${order.orderID}`
                }))
                : [];
            setOrders(mappedOrders);
            return mappedOrders;
        } catch {
            setOrders([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePackingNumber = async () => {
        const packingNum = Number(packingNumber);
        
        if (!packingNumber || isNaN(packingNum)) {
            showMessage('Error', 'Please enter a valid bin number', 'error');
            return;
        }

        if (packingNum < 1) {
            showMessage('Error', 'Bin number must be greater than or equal to 1', 'error');
            return;
        }

        if (!formik.values.orderId) {
            showMessage('Error', 'Please find and select an order first', 'error');
            return;
        }

        try {
            setIsLoading(true);
            await orderServices.saveOrderPacking({
                orderID: Number(formik.values.orderId),
                packingNo: packingNum
            });
            showMessage('Success', 'Bin Number Updated Successfully', 'success');

            const updatedDetails = await orderServices.GetPackingOrderByOrderId(Number(formik.values.orderId));
            setOrderDetails(updatedDetails);
            setPackingNumber(updatedDetails.packingNo || "");

        } catch (error) {
            showMessage('Error', 'Failed to update bin number', 'error');
            console.error('Error updating bin number:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOrderSelect = async (orderId: number) => {
        try {
            const details = await orderServices.GetPackingOrderByOrderId(orderId);
            if (details) {
                setOrderDetails(details);
                setPackingNumber(details.packingNo || "");
            } else {
                console.warn("No details found for order ID:", orderId);
                showMessage("Warning", "No details found for this order", "warn");
            }
        } catch (error) {
            console.error("Error while fetching order details:", error);
            showMessage("Error", "Failed to fetch order details", "error");
        }
    };

    const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
        toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
    };

    const formik = useFormik({
        initialValues: {
            orderId: null as number | null,
        },
        validate: (data) => {
            const errors: Record<string, string> = {};
            if (!data.orderId) {
                errors.orderId = 'Order ID Required';
            }
            return errors;
        },
        onSubmit: () => { },
    });

    return (
        <Suspense fallback={<AppLoading />}>
            <Toast ref={toast} />
            <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
                <div className="col-12">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-3">
                        <h5 className="ml-1 mb-0 pb-3" >{t("Update Bin No")}</h5>
                        <OrderIdSelector
                            formik={formik}
                            orders={orders}
                            packingNumber={packingNumber}
                            onPackingNumberChange={handlePackingNumberChange}
                            onUpdatePackingNumber={handleUpdatePackingNumber}
                            isLoading={isLoading}
                            onFindOrder={handleFindOrder}
                            orderIdInput={orderIdInput}
                            onOrderIdChange={handleOrderIdChange}
                        />
                    </div>
                </div>

                <div className="col-12">
                    {loading ? (
                        <AppLoading />
                    ) : (
                        <PackingNewOrderReview OrderData={orderDetails} />
                    )}
                </div>
            </div>
        </Suspense>
    );
};

export default Packingorders;
